import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { themeService, ThemeServiceContext } from '../../services/themeService';
import { userFrameService } from '../../services/userFrameService';
import * as api from '../../services/api';
import { io, Socket } from 'socket.io-client';
import { SOCKET_URL } from '../../config/api';
import { toast } from 'react-toastify';
import UserPageShell, { userCardEnterSx } from '../shared/UserPageShell';
import { getThemeDetailPath, ThemeChannel } from '../../utils/themePaths';
import CustomFrameLayoutPreview from '../shared/CustomFrameLayoutPreview';
import { rewriteToCdn } from '../../utils/cdn';

export interface ThemeFramesProps {
  channel?: ThemeChannel;
  userId?: string;
}

interface UserFrame {
  _id: string;
  name: string;
  description: string;
  frameImage: {
    url: string;
    path: string;
  };
  tags: string[];
  metadata: {
    originalFileName: string;
    fileSize: number;
    uploadDate: string;
  };
}

interface CustomFrame {
  _id: string;
  name: string;
  size: string;
  width?: number;
  height?: number;
  photoPositions: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
  }>;
  frameImage?: {
    url: string;
    path: string;
  };
}

const ThemeFrames: React.FC<ThemeFramesProps> = ({ channel = 'pro', userId }) => {
  const { themeId, customFrameId } = useParams<{ themeId: string; customFrameId: string }>();
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const apiCtx: ThemeServiceContext = useMemo(
    () => (userId ? { adminUserId: userId, channel } : { channel }),
    [userId, channel],
  );
  const themeDetailPath = themeId ? getThemeDetailPath(channel, themeId, userId) : '';
  const [customFrame, setCustomFrame] = useState<CustomFrame | null>(null);
  const [userFrames, setUserFrames] = useState<UserFrame[]>([]);
  const [allUserFrames, setAllUserFrames] = useState<UserFrame[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedFrames, setSelectedFrames] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (themeId && customFrameId) {
      loadData();
    }
  }, [themeId, customFrameId, channel, userId]);

  useEffect(() => {
    if (!themeId || userId) return;

    const socket: Socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      path: '/socket.io',
    });

    const joinUserRoom = () => socket.emit('join-user-room');
    socket.on('connect', joinUserRoom);
    socket.on('theme:content-changed', (data: {
      themeId?: string;
      customFrameId?: string;
      changeType?: string;
    }) => {
      if (data?.themeId !== themeId) return;

      if (data.changeType === 'custom-frame-removed') {
        if (!data.customFrameId || data.customFrameId === customFrameId) {
          navigate(themeDetailPath);
        }
        return;
      }

      if (data.customFrameId && data.customFrameId !== customFrameId) return;
      loadData();
    });

    return () => {
      socket.emit('leave-user-room');
      socket.off('connect', joinUserRoom);
      socket.off('theme:content-changed');
      socket.disconnect();
    };
  }, [themeId, customFrameId, navigate]);

  const loadData = async () => {
    if (!themeId || !customFrameId) return;

    try {
      setLoading(true);
      setError(null);
      const themeResponse = await themeService.getThemeCustomFrameFrames(
        themeId,
        customFrameId,
        apiCtx,
      );

      // Admin quản lý theme user → lấy pool frame của đúng user đó (không lấy frame của admin).
      let poolFrames: UserFrame[] = [];
      if (userId) {
        const assets = await api.getAdminUserCustomFrameAssets(
          userId,
          customFrameId,
          'user_frame',
          1,
          200,
          channel,
        );
        poolFrames = (assets.items || []).map((item) => ({
          _id: item._id,
          name: item.name,
          description: item.description || '',
          frameImage: item.frameImage
            ? { url: rewriteToCdn(item.frameImage.url), path: item.frameImage.path || '' }
            : { url: '', path: '' },
          tags: item.tags || [],
          metadata: {
            originalFileName: item.metadata?.originalFileName || '',
            fileSize: item.metadata?.fileSize || 0,
            uploadDate: item.metadata?.uploadDate || item.createdAt,
          },
        }));
      } else {
        const userFramesResponse = await userFrameService.getUserFrames({
          customFrameId,
          limit: 100,
          channel,
        });
        poolFrames = userFramesResponse.userFrames || [];
      }

      setCustomFrame(themeResponse.customFrame);
      setUserFrames(themeResponse.userFrames);
      setAllUserFrames(poolFrames);
    } catch (err: any) {
      const status = err.response?.status;
      if (status === 404) {
        navigate(themeDetailPath);
        return;
      }
      setError(err.response?.data?.message || 'Lỗi khi tải dữ liệu');
      toast.error('Lỗi khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFrames = async () => {
    if (!themeId || !customFrameId || selectedFrames.length === 0) return;

    try {
      await themeService.addUserFrames({
        themeId,
        customFrameId,
        userFrameIds: selectedFrames,
      }, apiCtx);
      toast.success(`Đã thêm ${selectedFrames.length} frames vào theme`);
      setAddDialogOpen(false);
      setSelectedFrames([]);
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Lỗi khi thêm frames');
    }
  };

  const handleRemoveFrame = async (userFrameId: string) => {
    if (!themeId || !customFrameId) return;

    try {
      await themeService.removeUserFrames({
        themeId,
        customFrameId,
        userFrameIds: [userFrameId],
      }, apiCtx);
      toast.success('Đã xóa frame khỏi theme');
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Lỗi khi xóa frame');
    }
  };

  const handleFrameSelect = (frameId: string, checked: boolean) => {
    if (checked) {
      setSelectedFrames([...selectedFrames, frameId]);
    } else {
      setSelectedFrames(selectedFrames.filter(id => id !== frameId));
    }
  };

  const toLocalFrame = (item: {
    _id: string;
    name?: string;
    description?: string;
    frameImage?: { url?: string; path?: string };
    tags?: string[];
    metadata?: { originalFileName?: string; fileSize?: number; uploadDate?: string };
    createdAt?: string;
  }): UserFrame => ({
    _id: String(item._id),
    name: item.name || '',
    description: item.description || '',
    frameImage: {
      url: rewriteToCdn(item.frameImage?.url || ''),
      path: item.frameImage?.path || '',
    },
    tags: item.tags || [],
    metadata: {
      originalFileName: item.metadata?.originalFileName || '',
      fileSize: item.metadata?.fileSize || 0,
      uploadDate: item.metadata?.uploadDate || item.createdAt || new Date().toISOString(),
    },
  });

  const handleUploadFrames = async (picked: File[]) => {
    if (!customFrameId || picked.length === 0) return;
    const files = picked.slice(0, 10);
    for (const file of files) {
      const isImage = file.type.startsWith('image/') || /\.(png|jpe?g)$/i.test(file.name);
      if (!isImage) {
        toast.error(`File ${file.name} không phải là file ảnh (JPEG, PNG)`);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`File ${file.name} vượt quá giới hạn 5MB`);
        return;
      }
    }

    try {
      setUploading(true);
      const created: UserFrame[] = userId
        ? (await api.uploadAdminUserCustomFrameAssets(
            userId,
            customFrameId,
            'user_frame',
            files,
            channel,
          )).items.map(toLocalFrame)
        : ((await userFrameService.uploadUserFrames(customFrameId, files, channel)).createdFrames || []).map(toLocalFrame);
      if (created.length > 0) {
        setAllUserFrames((prev) => [...created, ...prev]);
        setSelectedFrames((prev) => [
          ...created.map((frame) => frame._id),
          ...prev,
        ]);
      }
      toast.success(`Đã upload ${created.length} user frame`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Upload user frame thất bại');
    } finally {
      setUploading(false);
    }
  };

  const closeAddDialog = () => {
    if (uploading) return;
    setAddDialogOpen(false);
  };

  const availableFrames = allUserFrames.filter(
    frame => !userFrames.some(themeFrame => themeFrame._id === frame._id)
  );

  if (loading) {
    return (
      <UserPageShell>
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </UserPageShell>
    );
  }

  if (error || !customFrame) {
    return (
      <UserPageShell>
        <Alert severity="error">{error || 'Custom Frame không tồn tại'}</Alert>
      </UserPageShell>
    );
  }

  return (
    <UserPageShell>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          mb: { xs: 1.5, sm: 2.5 },
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            component="h1"
            fontWeight={800}
            noWrap
            title={customFrame.name}
            sx={{
              fontSize: { xs: '1.25rem', sm: '1.85rem' },
              letterSpacing: '-0.03em',
              lineHeight: 1.2,
            }}
          >
            {customFrame.name}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ mt: 0.4, fontSize: { xs: '0.8rem', sm: '0.95rem' }, fontWeight: 600 }}
          >
            Kích thước: {customFrame.size} • {userFrames.length} User Frames
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={!isMobile ? <AddIcon /> : undefined}
          onClick={() => setAddDialogOpen(true)}
          sx={{
            flexShrink: 0,
            borderRadius: 999,
            textTransform: 'none',
            fontWeight: 800,
            boxShadow: 'none',
            ...(isMobile ? { minWidth: 40, width: 40, height: 40, p: 0 } : { px: 2 }),
          }}
          aria-label="Thêm Frames"
        >
          {isMobile ? <AddIcon /> : 'Thêm Frames'}
        </Button>
      </Box>

      <Paper
        elevation={0}
        sx={{
          mb: { xs: 2, sm: 3 },
          overflow: 'hidden',
          borderRadius: 3,
          border: '1px solid',
          borderColor: alpha(muiTheme.palette.primary.main, 0.14),
          bgcolor: alpha(muiTheme.palette.primary.main, 0.05),
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: { xs: 2, sm: 2.5 },
          px: 2,
        }}
      >
        <CustomFrameLayoutPreview
          width={customFrame.width}
          height={customFrame.height}
          size={customFrame.size}
          photoPositions={customFrame.photoPositions}
          maxHeight={{ xs: 220, sm: 300 }}
        />
      </Paper>

      <Typography
        fontWeight={800}
        sx={{ fontSize: { xs: '0.95rem', sm: '1.15rem' }, mb: { xs: 1.25, sm: 2 } }}
      >
        User Frames trong Theme
      </Typography>

      {userFrames.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            textAlign: 'center',
            borderRadius: 3,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <ImageIcon sx={{ fontSize: 52, color: 'text.secondary', mb: 1.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Chưa có User Frame nào
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Thêm user frames vào theme này
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800, boxShadow: 'none' }}
          >
            Thêm Frames
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={{ xs: 1.25, sm: 2.5 }}>
          {userFrames.map((frame, index) => (
            <Grid item xs={6} sm={6} md={4} key={frame._id}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                  position: 'relative',
                  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                  ...userCardEnterSx(index),
                  '&:hover': {
                    transform: { sm: 'translateY(-2px)' },
                    boxShadow: `0 10px 24px ${alpha(muiTheme.palette.primary.main, 0.16)}`,
                  },
                }}
              >
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveFrame(frame._id)}
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    zIndex: 1,
                    width: 30,
                    height: 30,
                    bgcolor: 'rgba(255,255,255,0.92)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                    '&:hover': { bgcolor: 'error.main', color: 'white' },
                  }}
                  aria-label="Xóa frame"
                >
                  <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <CardMedia
                  sx={{
                    height: { xs: 120, sm: 180 },
                    bgcolor: alpha(muiTheme.palette.primary.main, 0.05),
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                  }}
                  image={frame.frameImage.url}
                  title={frame.name}
                />
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 1.5 } }}>
                  <Typography
                    fontWeight={800}
                    noWrap
                    title={frame.name}
                    sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}
                  >
                    {frame.name}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={addDialogOpen}
        onClose={(_event, reason) => {
          if (uploading) return;
          if (reason === 'backdropClick' && document.activeElement === fileInputRef.current) return;
          closeAddDialog();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 1.5, sm: 2 },
            width: { xs: 'calc(100% - 24px)', sm: '100%' },
            maxHeight: { xs: '88vh', sm: '90vh' },
            borderRadius: 3.5,
            overflow: 'hidden',
            backgroundImage: `linear-gradient(180deg, ${alpha(muiTheme.palette.primary.main, 0.07)} 0%, #fff 24%)`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: { xs: '1.1rem', sm: '1.3rem' }, letterSpacing: '-0.02em', pt: 2.25, px: { xs: 2, sm: 3 } }}>
          Thêm User Frames
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Chọn các user frames để thêm vào theme, hoặc upload thêm frame mới
          </Typography>
          <Paper
            elevation={0}
            onClick={() => {
              if (uploading || !customFrameId) return;
              fileInputRef.current?.click();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.5,
              mb: 2,
              borderRadius: 2.5,
              border: '1.5px solid',
              borderColor: alpha('#ff8ba7', 0.35),
              background: `linear-gradient(135deg, ${alpha('#ff8ba7', 0.18)} 0%, ${alpha('#ff8ba7', 0.08)} 100%)`,
              cursor: uploading ? 'default' : 'pointer',
              opacity: uploading ? 0.7 : 1,
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,.png,.jpg,.jpeg"
              multiple
              style={{ display: 'none' }}
              disabled={uploading || !customFrameId}
              onChange={(e) => {
                const files = e.target.files ? Array.from(e.target.files) : [];
                e.target.value = '';
                handleUploadFrames(files);
              }}
            />
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#ff8ba7', 0.35),
                color: '#e86a8a',
                flexShrink: 0,
              }}
            >
              {uploading ? <CircularProgress size={22} sx={{ color: '#e86a8a' }} /> : <CloudUploadIcon />}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: '#e86a8a', fontSize: '0.95rem' }}>
                {uploading ? 'Đang upload user frame...' : 'Upload user frame'}
              </Typography>
              <Typography variant="body2" sx={{ color: alpha('#e86a8a', 0.75) }}>
                PNG, JPG. Tối đa 10 file, mỗi file 5MB.
              </Typography>
            </Box>
          </Paper>

          {availableFrames.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Không có user frames nào để thêm
            </Typography>
          ) : (
            <Grid container spacing={{ xs: 1.25, sm: 2 }}>
              {availableFrames.map((frame) => {
                const selected = selectedFrames.includes(frame._id);
                return (
                  <Grid item xs={6} sm={6} md={4} key={frame._id}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        borderRadius: 2.5,
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        boxShadow: selected
                          ? `0 8px 18px ${alpha(muiTheme.palette.primary.main, 0.2)}`
                          : 'none',
                        overflow: 'hidden',
                      }}
                      onClick={() => handleFrameSelect(frame._id, !selected)}
                    >
                      <CardMedia
                        sx={{
                          height: { xs: 120, sm: 160 },
                          bgcolor: alpha(muiTheme.palette.primary.main, 0.05),
                          backgroundSize: 'contain',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'center',
                          position: 'relative',
                        }}
                        image={frame.frameImage.url}
                        title={frame.name}
                      >
                        <Checkbox
                          checked={selected}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleFrameSelect(frame._id, e.target.checked);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'rgba(255,255,255,0.92)',
                            borderRadius: '50%',
                            p: 0.4,
                          }}
                        />
                      </CardMedia>
                      <CardContent sx={{ py: 1, px: 1, '&:last-child': { pb: 1 } }}>
                        <Typography
                          noWrap
                          fontWeight={700}
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' }, textAlign: 'center' }}
                        >
                          {frame.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2, gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
          <Button
            onClick={closeAddDialog}
            disabled={uploading}
            fullWidth={isMobile}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleAddFrames}
            variant="contained"
            disabled={selectedFrames.length === 0 || uploading}
            fullWidth={isMobile}
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 999, boxShadow: 'none' }}
          >
            Thêm {selectedFrames.length} Frames
          </Button>
        </DialogActions>
      </Dialog>
    </UserPageShell>
  );
};

export default ThemeFrames;
